/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/*
 * The file is automatically generated by Serenity UI.
 *
 * ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
 *
 * Found an issue? Submit a task to the ClickUp board:
 * https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
 */
import type { Axis } from '@react-types/overlays';
import * as stylex from '@stylexjs/stylex';
import { createContext, type ForwardedRef, forwardRef } from 'react';
import type { ModalOverlayProps } from 'react-aria-components';
import {
  type ContextValue,
  Modal as ModalCore,
  ModalOverlay as ModalOverlayCore,
  useContextProps,
} from 'react-aria-components';
import { zIndices } from '../../constants.stylex';
import { $ } from '../../tokens.stylex';

interface DrawerProps extends Omit<ModalOverlayProps, 'style' | 'className'> {
  /** The placement of the drawer on the screen */
  placement?: Axis;
  style?: stylex.StyleXStyles;
}

const DrawerContext = createContext<ContextValue<DrawerProps, HTMLDivElement>>({});

const Drawer = forwardRef((props: DrawerProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, DrawerContext);

  const { placement = 'left', style, children, isDismissable = true, ...coreProps } = props;

  return (
    <ModalOverlayCore ref={ref} isDismissable={isDismissable} {...stylex.props(overlayStyles.base)} {...coreProps}>
      {(renderProps) => (
        <ModalCore data-placement={placement} {...stylex.props(styles.base, styles[placement], style)} {...renderProps}>
          {children}
        </ModalCore>
      )}
    </ModalOverlayCore>
  );
});

const slideInFromLeft = stylex.keyframes({
  '0%': {
    opacity: 0,
    transform: 'translateX(-100%)',
  },
  '100%': {
    opacity: 1,
    transform: 'translateX(0)',
  },
});

const slideOutToLeft = stylex.keyframes({
  '0%': {
    opacity: 1,
    transform: 'translateX(0)',
  },
  '100%': {
    opacity: 0,
    transform: 'translateX(-100%)',
  },
});

const slideInFromRight = stylex.keyframes({
  '0%': {
    opacity: 0,
    transform: 'translateX(100%)',
  },
  '100%': {
    opacity: 1,
    transform: 'translateX(0)',
  },
});

const slideOutToRight = stylex.keyframes({
  '0%': {
    opacity: 1,
    transform: 'translateX(0)',
  },
  '100%': {
    opacity: 0,
    transform: 'translateX(100%)',
  },
});

const slideInFromTop = stylex.keyframes({
  '0%': {
    opacity: 0,
    transform: 'translateY(-100%)',
  },
  '100%': {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

const slideOutToTop = stylex.keyframes({
  '0%': {
    opacity: 1,
    transform: 'translateY(0)',
  },
  '100%': {
    opacity: 0,
    transform: 'translateY(-100%)',
  },
});

const slideInFromBottom = stylex.keyframes({
  '0%': {
    opacity: 0,
    transform: 'translateY(100%)',
  },
  '100%': {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

const slideOutToBottom = stylex.keyframes({
  '0%': {
    opacity: 1,
    transform: 'translateY(0)',
  },
  '100%': {
    opacity: 0,
    transform: 'translateY(100%)',
  },
});

const styles = stylex.create({
  base: {
    outline: 'none',
    overflow: 'hidden',
    animationDuration: '225ms',
    animationFillMode: 'forwards',
    animationTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
    backgroundColor: $.surfaceBGSecondary,
    boxShadow: $.shadowCenter080Loud,
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    zIndex: zIndices.modal,
  },
  left: {
    animationName: {
      default: null,
      ':is([data-entering])': slideInFromLeft,
      ':is([data-exiting])': slideOutToLeft,
    },
    height: '100vh',
    left: 0,
    top: 0,
  },
  right: {
    animationName: {
      default: null,
      ':is([data-entering])': slideInFromRight,
      ':is([data-exiting])': slideOutToRight,
    },
    height: '100vh',
    right: 0,
    top: 0,
  },
  top: {
    animationName: {
      default: null,
      ':is([data-entering])': slideInFromTop,
      ':is([data-exiting])': slideOutToTop,
    },
    left: 0,
    right: 0,
    top: 0,
    width: '100vw',
  },
  bottom: {
    animationName: {
      default: null,
      ':is([data-entering])': slideInFromBottom,
      ':is([data-exiting])': slideOutToBottom,
    },
    bottom: 0,
    left: 0,
    right: 0,
    width: '100vw',
  },
});

const overlayStyles = stylex.create({
  base: {
    alignItems: 'center',
    backgroundColor: $.opacity48,
    display: 'flex',
    justifyContent: 'center',
    opacity: {
      default: 1,
      ':is([data-entering], [data-exiting])': 0,
    },
    position: 'fixed',
    transitionDuration: '225ms',
    transitionProperty: 'opacity',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 999,
    height: '100vh',
    left: 0,
    top: 0,
    width: '100vw',
  },
});

export { Drawer, DrawerContext, type DrawerProps };
